import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { NSBadge, NSButton, NSInput } from 'bricks';
import { ConfirmationModal } from 'components/ConfirmationModal/ConfirmationModal';
import useToast, { ToastType } from 'hooks/useToast';
import { useInviteUserToOrganization } from 'views/ProFormaTable/hooks/useInviteUserToOrganization';
import { useShareProForma } from 'views/ProFormaTable/hooks/useShareProForma';
import { useAuthContext } from 'contexts/AuthContext';
import invariant from 'helpers/invariant';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IProFormaGeneralInfo } from 'views/ProFormaTable/types';

const MAX_MESSAGE_LENGTH = 300;
const DEAL_MANAGER_FUNCTION_ID = 7;
const GENERIC_ERROR_MSG = 'Failed to share pro forma. Please try again.';

interface IShareFormValues {
    userName: string;
    email: string;
    message: string;
}

const validationSchema = Yup.object({
    userName: Yup.string().trim().required('Enter a user name'),
    email: Yup.string().trim().email('Enter a valid email address').required('Enter a valid email address'),
    message: Yup.string().max(MAX_MESSAGE_LENGTH),
});

export interface IShareProFormaModalProps {
    isOpen: boolean;
    toggle: () => void;
    proForma: IProFormaGeneralInfo;
}

const ShareProFormaModal = ({
    isOpen,
    toggle,
    proForma,
}: IShareProFormaModalProps) => {
    const { selectedOrganizationId } = useAuthContext();
    invariant(selectedOrganizationId, 'Selected organization ID is required');

    const { showError, showSuccess, showToast } = useToast();
    const inviteMutation = useInviteUserToOrganization();
    const shareMutation = useShareProForma();
    const isSubmitting = inviteMutation.isPending || shareMutation.isPending;

    const handleInviteError = (error: Error) => {
        const status = (error as AxiosError).response?.status;

        if (status === 409) {
            showToast('This person is already a part of your organization', ToastType.Info);
            return;
        }
        if (status === 403) {
            showError("You don't have access to share pro formas. Please contact your organization admin.");
            return;
        }
        showError(GENERIC_ERROR_MSG);
    };

    const onSubmit = ({ userName, email, message }: IShareFormValues) => {
        const trimmedEmail = email.trim();
        // Invite user to organization first; only send the share email if that succeeds.
        inviteMutation.mutate(
            {
                organizationId: selectedOrganizationId,
                payload: {
                    userEmail: trimmedEmail,
                    userName: userName.trim(),
                    userFunction: DEAL_MANAGER_FUNCTION_ID,
                },
            },
            {
                onSuccess: () => {
                    shareMutation.mutate(
                        { dealId: proForma.dealId, proFormaId: proForma.id, payload: { recipientEmail: trimmedEmail, personalMessage: message } },
                        {
                            onSuccess: () => {
                                showSuccess(`Invitation sent to ${trimmedEmail}`);
                                toggle();
                            },
                            onError: () => showError(GENERIC_ERROR_MSG),
                        },
                    );
                },
                onError: handleInviteError,
            },
        );
    };

    const formik = useFormik<IShareFormValues>({
        initialValues: { userName: '', email: '', message: '' },
        validationSchema,
        validateOnMount: true,
        onSubmit,
    });

    return (
        <ConfirmationModal
            isOpen={isOpen}
            toggle={toggle}
            modalHeaderText='Share pro forma'
            size="md"
            customFooter={(
                <>
                    <NSButton
                        outline
                        callback={toggle}
                        text="Cancel"
                        className="btn-secondary mr-2"
                    />
                    <NSButton
                        color="primary"
                        icon={faUserPlus}
                        callback={() => formik.handleSubmit()}
                        text="Send invite"
                        disabled={!formik.dirty || !formik.isValid}
                        isLoading={isSubmitting}
                    />
                </>
            )}
        >
            <p className="mb-3">
                Invite a teammate to collaborate on
                {' '}
                <strong>{proForma.name}</strong>
                {proForma.dealName ? ` (${proForma.dealName})` : ''}.
                They will receive an email invite to join your organization and be added to this deal.
            </p>
            <NSInput
                name="userName"
                type="text"
                label="Teammate's name"
                required
                placeholder="Teammate's name"
                formikProps={formik}
                invalid={!!(formik.touched.userName && formik.errors.userName)}
            />
            <NSInput
                name="email"
                type="email"
                label="Teammate's email"
                labelClassName="mt-1"
                required
                placeholder="teammate@company.com"
                formikProps={formik}
                invalid={!!(formik.touched.email && formik.errors.email)}
            />
            <NSInput
                name="message"
                type="textarea"
                label="Personal message (optional)"
                labelClassName="mt-1"
                placeholder="Hey — take a look at this pro forma when you get a chance."
                formikProps={formik}
                maxLength={MAX_MESSAGE_LENGTH}
                rows={3}
            />
            <div className="mt-3">
                <span className="text-muted small mr-2">They&apos;ll join as</span>
                <NSBadge color="primary-lighten">
                    Deal Manager
                </NSBadge>
                <span className="text-muted small ml-2">— full read &amp; write access to this deal.</span>
            </div>
        </ConfirmationModal>
    );
};

export default ShareProFormaModal;